import React, {useMemo, useState} from 'react';
import {Alert, Autocomplete, Box, Button, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import request from '../util/SuperagentUtils';
import {useReactiveGetRequest} from '../hooks/request/useReactiveGetRequest';
import {useAutocompleteOptions} from '../hooks/autocomplete/useAutocompleteOptions';
import {AutocompleteOption} from '../components/SortFilter/interfaces';
import {getErrorMessageForStandardResponse} from '../util/NetworkErrorUtil';
import Busy from '../components/Busy';
import {Account} from '../interfaces';

interface AccountRefVO {
  id: string;
  companyName: string;
  email: string;
  // Account.getType() value from the backend (e.g. "Buyer" / "Supplier").
  // Admin accounts are already excluded server-side.
  type?: string;
  username?: string;
}

interface LoginAsAccountProps {
  handleAccountChange: (account: Account) => void;
}

const resolveAccountType = (ref: AccountRefVO): string | undefined => (
  ref.type && ref.type.trim().length > 0 ? ref.type : undefined
);

const LoginAsAccount = ({handleAccountChange}: LoginAsAccountProps) => {
  const {data: accountRefs, isLoading: isLoadingAccounts, error: loadError} = useReactiveGetRequest<AccountRefVO[]>({
    requestUrl: '/api/account/login-as-accounts',
  });
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  // Only offer the type filter if the data actually carries a recognizable
  // type field — see resolveAccountType() above.
  const availableAccountTypes = useMemo(() => {
    const types = new Set<string>();
    (accountRefs || []).forEach((ref) => {
      const type = resolveAccountType(ref);
      if (type) types.add(type);
    });
    return Array.from(types).sort();
  }, [accountRefs]);

  const filteredAccountRefs = useMemo(
    () => (selectedType ? (accountRefs || []).filter((ref) => resolveAccountType(ref) === selectedType) : accountRefs || []),
    [accountRefs, selectedType],
  );

  const handleTypeChange = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
    setSelectedType(value ?? '');
    setSelectedAccountId(null);
  };

  // Keyed lookup so renderOption can show company name and email on separate
  // lines without baking extra fields onto the option shape (kept as the plain
  // {id, label} contract shared by useAutocompleteOptions/AutocompleteOption).
  const accountsById = useMemo(() => new Map(filteredAccountRefs.map(ref => [ref.id, ref])), [filteredAccountRefs]);

  const accountOptions = useMemo<AutocompleteOption[]>(() => filteredAccountRefs.map(ref => {
    // Fold username into the searchable label (but not necessarily the
    // visible text -- see renderOption) so admins can find an account by
    // whichever one they know, without showing a redundant duplicate value
    // when the username matches the email.
    const searchParts = [ref.companyName, ref.email];
    if (ref.username && ref.username !== ref.email) searchParts.push(ref.username);
    return {id: ref.id, label: searchParts.join(' - ')};
  }), [filteredAccountRefs]);

  const {
    handleGetOptionLabel,
    handleIsOptionEqualToValue,
    filterOptions,
    resolvedValue,
  } = useAutocompleteOptions({autocompleteOptions: accountOptions, value: selectedAccountId, filterMode: 'flexible'});

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedAccountId) return;

    setSubmitting(true);
    setSubmitError('');
    Busy.set(true);
    request.post('/api/login-as-account').send({id: selectedAccountId}).then(response => {
      Busy.set(false);
      // updateUserAndRedirect (passed in as handleAccountChange) already pushes
      // to the new account's landing page via react-router history, so there is
      // nothing left to redirect here.
      handleAccountChange(response.body);
    }).catch(err => {
      Busy.set(false);
      setSubmitting(false);
      setSubmitError(getErrorMessageForStandardResponse(err, 'Unable to log in as this account.'));
    });
  };

  return (
    <Box sx={{display: 'flex', justifyContent: 'center', px: 2, py: {xs: 4, md: 8}}}>
      <Paper elevation={2} sx={{maxWidth: 640, width: '100%', p: {xs: 3, md: 5}, borderRadius: '4px'}}>
        <Typography variant="h6" sx={{mb: 1}}>Log In As Another Account</Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          Search by company name, email, or username, then choose an account to log in as.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {loadError && (
            <Alert severity="error" sx={{mb: 2}}>
              {getErrorMessageForStandardResponse(loadError, 'Unable to load the account list. Please refresh and try again.')}
            </Alert>
          )}
          {availableAccountTypes.length > 0 && (
            <Box sx={{mb: 2.5}}>
              <Typography
                variant="caption"
                sx={{display: 'block', mb: 0.75, fontWeight: 500, color: 'text.secondary'}}
              >
                Account type
              </Typography>
              <ToggleButtonGroup
                value={selectedType}
                exclusive
                size="small"
                onChange={handleTypeChange}
                aria-label="Filter accounts by type"
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: '0.94rem',
                    lineHeight: 1.5,
                    color: 'text.secondary',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    px: 2.5,
                    '&:hover': {
                      backgroundColor: 'neutral.light',
                    },
                    '&.Mui-selected': {
                      color: '#fff',
                      backgroundColor: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="" aria-label="All account types">All</ToggleButton>
                {availableAccountTypes.map((type) => (
                  <ToggleButton key={type} value={type} aria-label={`${type} accounts`}>
                    {type}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}
          <Autocomplete
            options={accountOptions}
            loading={isLoadingAccounts}
            value={resolvedValue ?? null}
            onChange={(_event, newValue) => setSelectedAccountId((newValue as AutocompleteOption | null)?.id ?? null)}
            getOptionLabel={handleGetOptionLabel}
            isOptionEqualToValue={handleIsOptionEqualToValue}
            filterOptions={filterOptions}
            disabled={submitting}
            renderOption={(liProps, option) => {
              const ref = accountsById.get(option.id);
              return (
                <li {...liProps} key={option.id} style={{alignItems: 'flex-start'}}>
                  <Box sx={{display: 'flex', flexDirection: 'column', minWidth: 0, py: 0.5}}>
                    <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
                      {ref?.companyName ?? option.label}
                    </Typography>
                    {ref?.email && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{wordBreak: 'break-word', whiteSpace: 'normal'}}
                      >
                        {ref.email}
                      </Typography>
                    )}
                    {ref?.username && ref.username !== ref.email && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{wordBreak: 'break-word', whiteSpace: 'normal', fontStyle: 'italic'}}
                      >
                        Username: {ref.username}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Company name, email, or username"
                placeholder="Start typing to search"
                autoFocus
                fullWidth
              />
            )}
          />

          {submitError && <Alert severity="error" sx={{mt: 2}}>{submitError}</Alert>}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            startIcon={<LoginIcon />}
            disabled={!selectedAccountId || submitting}
            sx={{mt: 3}}
          >
            Log In As Account
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginAsAccount;
